import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '../../../features/auth/authSlice';
import { useAddFavoriteMutation, useDeleteFavoriteMutation } from '../../../features/api/apiSlice';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { useToast } from '../../../components/ui/Toast'; // Import useToast

// Helper to format the price
const formatPrice = (price) => {
  const num = parseInt(price, 10);
  if (isNaN(num)) return 'Price not available';
  return `UGX ${num.toLocaleString()}`;
};

// Helper to find a specific parameter
const findParameter = (parameters, name) => {
  const param = parameters?.find(p => p.name.toLowerCase() === name.toLowerCase());
  return param ? param.value : null;
};

const PropertyCard = ({ property, isFavorite }) => {
  if (!property) return null;

  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const addToast = useToast(); // Initialize useToast

  const {
    id,
    slug_id,
    title,
    price,
    address,
    property_type,
    title_image,
    is_premium,
    parameters,
    post_created,
  } = property;

  const bedrooms = findParameter(parameters, 'bedrooms');
  const bathrooms = findParameter(parameters, 'bathrooms');
  const area = findParameter(parameters, 'area') || findParameter(parameters, 'sqft');

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      addToast('Please log in to save properties.', 'info'); // Show info toast
      navigate('/login');
      return;
    }

    try {
      if (isFavorite) {
        await deleteFavorite(id).unwrap();
        addToast('Property removed from favorites.', 'info'); // Show info toast
      } else {
        await addFavorite(id).unwrap();
        addToast('Property added to favorites!', 'success'); // Show success toast
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      addToast('Failed to update favorite status. Please try again.', 'error'); // Use toast for error
    }
  };

  const handleShare = async (property) => {
    const shareUrl = `${window.location.origin}/property/${property.slug_id}`;
    const shareData = {
      title: property.title,
      text: `Check out this property: ${property.title}`,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        addToast('Property shared successfully!', 'success');
      } catch (error) {
        console.error('Error sharing:', error);
        addToast('Failed to share property.', 'error');
      }
    } else {
      // Fallback for browsers that do not support the Web Share API
      try {
        await navigator.clipboard.writeText(shareUrl);
        addToast('Property link copied to clipboard!', 'info');
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        addToast('Failed to copy link to clipboard.', 'error');
      }
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300 group">
      <div className="relative">
        <Link to={`/property/${slug_id}`} className="block">
          <img
            src={title_image}
            alt={title}
            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </Link>

        <div className="absolute top-2 left-2 flex flex-col gap-2">
          <Badge variant="secondary" className="capitalize">
            For {property_type}
          </Badge>
          {is_premium && (
            <Badge variant="premium">
              <Icon name="Star" className="mr-1" size={12} />
              Taken
            </Badge>
          )}
        </div>

        <div className="absolute top-2 right-2">
          <Button
            variant="ghost"
            size="icon"
            className="bg-background/60 hover:bg-background rounded-full"
            onClick={handleToggleFavorite}
            aria-label="Toggle Favorite"
          >
            <Icon
              name="Heart"
              className={`transition-colors ${isFavorite ? 'text-red-500 fill-current' : 'text-card-foreground'}`}
            />
          </Button>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-foreground truncate hover:text-primary">
          <Link to={`/property/${slug_id}`}>{title}</Link>
        </h3>

        <p className="text-sm text-muted-foreground truncate mt-1">
          <Icon name="MapPin" className="inline-block mr-1 -mt-1" size={14} />
          {address}
        </p>

        <div className="mt-4 text-lg font-bold text-primary">
          {formatPrice(price)}
        </div>

        <div className="mt-4 pt-4 border-t border-border flex justify-between items-center text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            {bedrooms && (
              <span className="flex items-center gap-1.5" title={`${bedrooms} Bedrooms`}>
                <Icon name="BedDouble" size={16} />
                {bedrooms}
              </span>
            )}
            {bathrooms && (
              <span className="flex items-center gap-1.5" title={`${bathrooms} Bathrooms`}>
                <Icon name="Bath" size={16} />
                {bathrooms}
              </span>
            )}
            {area && (
              <span className="flex items-center gap-1.5" title={`${area} sq ft`}>
                <Icon name="Square" size={16} />
                {area}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2"> {/* New div to group date and share button */}
            <span className="text-xs">{post_created}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => handleShare(property)}
              aria-label="Share Property"
            >
              <Icon name="Share2" size={16} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
